/** The catalog records' re-key question (plan O11) as the records core asks it — the
 *  activity's and the itinerary's: a Custom ID change on a record an import has touched raises
 *  the catalog's `rekey_import_matching` ("Re-key import matching to FH-2? FareHarbor's next
 *  sync will create a new record."), asked under the field and in the ledger in the server's
 *  words; Save waits for it.
 *
 *  - **Re-key** is the answer, bound to the Custom ID it was given for and sent with it
 *    (`rekey_import_matching_to`, the catalog's `value` option that `confirms` it), in the
 *    form the server compares (`confirmedCustomId`); any later change of the Custom ID asks
 *    again (the records core's confirmed answers, `choices.ts`).
 *  - **Cancel** takes the Custom ID change back — so the server's refusal of an answered
 *    Cancel never arises.
 *  - Asked up front only on a record an import has touched (`rekeyAskedUpFront`); the
 *    preview decides. */
import { REKEY_IMPORT_MATCHING } from '@/api/catalogRecord';
import type { RecordOption } from '@/features/records/editor/types';

import { normalizeCustomId } from './identity';

/** The re-key answer that keeps the Custom ID: the change is taken back, never sent. */
export const REKEY_CANCEL = 'cancel';

/** A confirmed Custom ID as the server compares it: stripped, blank = null. */
export function confirmedCustomId(value: unknown): string | null {
  return normalizeCustomId(typeof value === 'string' ? value : null);
}

/** A catalog's choice questions as the editor asks them: the re-key question in the
 *  preview's words (its `reason` names the new Custom ID and the source), its Cancel taking
 *  the Custom ID change back; the Custom ID a Re-key confirms in the identity rule's form. */
export function rekeyQuestionOptions(options: readonly RecordOption[]): RecordOption[] {
  return options.map((o): RecordOption => {
    if (o.key === REKEY_IMPORT_MATCHING) {
      return {
        ...o,
        reasonIsQuestion: true,
        values: o.values.map((v) => (v.value === REKEY_CANCEL ? { ...v, reverts: true } : v)),
      };
    }
    return o.type === 'value' ? { ...o, normalize: confirmedCustomId } : o;
  });
}

/** Whether a raised question is asked before the preview says so: the re-key question only on
 *  a record an import has touched (`imported`) — the preview decides. */
export function rekeyAskedUpFront(imported: boolean) {
  return (option: RecordOption): boolean | undefined => (option.key === REKEY_IMPORT_MATCHING ? imported : undefined);
}
